import React from 'react';
import { FloComponentProps, FloMenuButton, FloMenuButtonItemType } from 'components';
import { isAccountFullAccess, LocationFacet, LocationFilters, LocationType, SystemMode, User, Location } from 'model';
import styles from './LocationActions.module.css';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FloButton, FloButtonType } from 'components/FloButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Row } from 'react-bootstrap';
import { LocationFilters as LocationFiltersComp } from './LocationFilters';
import { LocationFacetsData, LocationFilterState } from 'model/state';

interface LocationActionsProps extends FloComponentProps {
  isSelectedAny: boolean;
  onAddLocationClick: (type: LocationType) => void;
  onAddBulkUnitClick: () => void;
  onAddToClick: (type: LocationType) => void;
  onRemoveClick: () => void;
  selectedNumber: number;
  onSetThresholdsClick: () => void;
  onAutoShutoffClick: () => void;
  onSetAlarmPreferencesClick: () => void;
  filterData: LocationFacetsData;
  filters: LocationFilterState;
  updateLocationFilters: (filters: LocationFilters) => void;
  locationsCount: number;
  onLocationSleepClick: (hours: number) => void;
  onLocationChangeModeClick: (target: SystemMode) => void;
  loggedUser?: User;
  areAllSelectedFullAccessLocations: boolean;
  setShowLocationFilters: (show: boolean) => void;
  showFilters: boolean;
  fetchLocationFacets: (facet: LocationFacet) => void;
  isRootLevel: boolean;
  parent: Location | null;
}

export const LocationActions: React.FC<LocationActionsProps> = React.memo(({
  className,
  isSelectedAny,
  onAddLocationClick,
  onAddBulkUnitClick,
  onAddToClick,
  selectedNumber,
  onRemoveClick,
  onSetThresholdsClick,
  onAutoShutoffClick,
  filterData,
  filters,
  updateLocationFilters,
  locationsCount,
  onSetAlarmPreferencesClick,
  onLocationSleepClick,
  loggedUser,
  areAllSelectedFullAccessLocations,
  onLocationChangeModeClick,
  setShowLocationFilters,
  showFilters,
  fetchLocationFacets,
  isRootLevel,
  parent,
}) => {
  const { t } = useTranslation();
  const getAddLocationMenuOptions = () => {
    const baseOptions = [
      { id: 'unit', onClick: () => onAddLocationClick(LocationType.UNIT) ,title: t('Unit'), description: t('add-unit-button-description') },
    ]
    if (parent?.type === LocationType.PROPERTY || parent?.type === LocationType.BUILDING) {
      return [
        ...baseOptions,
        { id: 'bulk-unit', onClick: () => onAddBulkUnitClick(), title: t('Bulk Units'), description: t('add-bulk-units-button-description') },
      ]
    }
    return [
      ...baseOptions,
      { id: 'building', onClick: () => onAddLocationClick(LocationType.BUILDING) ,title: t('Building'), description: t('add-building-button-description') },
      { id: 'property', onClick: () => onAddLocationClick(LocationType.PROPERTY) ,title: t('Property'), description: t('add-property-button-description') },
      { id: 'community', onClick: () => onAddLocationClick(LocationType.REGION) ,title: t('Community'), description: t('add-community-button-description') },
    ];
  }

  return (
    <Row
      className = {classnames(className, styles.root)}
    >
      { !isSelectedAny ?
        <Col xs="12">
          <Row>
            <Col xs="12">
              { isAccountFullAccess(loggedUser) &&
                <FloMenuButton
                  className   = "float-right"
                  text        = {t('Add Location')}
                  name        = "add-location-btn"
                  menuItems   = {getAddLocationMenuOptions()}
                />
              }
              <FloButton
                className = {classnames('float-right', styles.filterButton)}
                text      = {t('Filters', { count: locationsCount })}
                onClick   = {() => { setShowLocationFilters(!showFilters) }}
                type      = {FloButtonType.SECONDARY}
                icon      = {<FontAwesomeIcon className={styles.icon} icon="filter"/> }
              />
            </Col>
          </Row>
          { showFilters && <LocationFiltersComp
              filterData            = {filterData}
              filters               = {filters}
              updateLocationFilters = {updateLocationFilters}
              fetchLocationFacets   = {fetchLocationFacets}
              userId                = {loggedUser?.id}
              isRootLevel           = {isRootLevel}
            />
          }
        </Col>
        :
        <Col xs="12">
          <FloMenuButton
            className   = "float-right"
            text        = {t('Actions')}
            name        = "actions-location-btn"
            menuItems   = {[
              { id: 'set-thresholds', onClick: onSetThresholdsClick, title: t('add-to-set-thresholds'), disabled: !areAllSelectedFullAccessLocations },
              { id: 'auto-shutoff', onClick: onAutoShutoffClick, title: t('Auto Shutoff'), disabled: !areAllSelectedFullAccessLocations },
              { id: 'set-alarm-preferences', onClick: onSetAlarmPreferencesClick, title: t('set-alarm-preferences') },
              { id: 'add-to-property', onClick: () => onAddToClick(LocationType.PROPERTY), title: t('Add to Property'), disabled: !areAllSelectedFullAccessLocations },
              { id: 'add-to-building', onClick: () => onAddToClick(LocationType.BUILDING), title: t('Add to Building'), disabled: !areAllSelectedFullAccessLocations },
              { id: 'add-to-community', onClick: () => onAddToClick(LocationType.REGION), title: t('Add to Community'), disabled: !areAllSelectedFullAccessLocations },
              { id: 'remove', onClick: () => { onRemoveClick() }, title: t('add-to-remove-items'), disabled: !areAllSelectedFullAccessLocations },
              { id: 'divider', title: 'divider', type: FloMenuButtonItemType.DIVIDER },
              { id: 'sleep-24', title: t('Sleep 24 hours'), onClick: () => onLocationSleepClick(24), disabled: !areAllSelectedFullAccessLocations },
              { id: 'sleep-72', title: t('Sleep 72 hours'), onClick: () => onLocationSleepClick(72), disabled: !areAllSelectedFullAccessLocations },
              { id: 'home-mode', title: t('Set to Home Mode'), onClick: () => onLocationChangeModeClick(SystemMode.HOME), disabled: !areAllSelectedFullAccessLocations },
              { id: 'away-mode', title: t('Set to Away Mode'), onClick: () => onLocationChangeModeClick(SystemMode.AWAY), disabled: !areAllSelectedFullAccessLocations },
            ]}
          />
          <span className = {classnames("float-right", styles.itemsSelected)} >{t('selected-items', { count: selectedNumber })}</span>
        </Col>
      }
    </Row>
  )
})
